import {apiBase} from '../cities/city-data';
import {PublicEvent} from '../games/public-game';

/** One of the biggest upcoming public events, as GET /stats/featured-events returns them. */
export interface FeaturedEvent {
    event: PublicEvent & {organizer: {name?: string | null; username: string; avatarUrl?: string | null}};
    /** Every sub-event at any depth: tables, tournaments, booths, sub-meetups. */
    activityCount: number;
}

/** The rows the home page lists; the server picks that many of the biggest events, never more. */
export const EVENT_ROWS = 5;

/**
 * Backoff between attempts. The API is redeployed often and a rollout leaves a few seconds of 503s;
 * without these a single blip during the prerender would bake a home page with no events at all.
 */
const RETRY_DELAYS_MS = [1_000, 3_000];

/**
 * The events the home page puts on show: the upcoming public ones with the most people going,
 * soonest first, picked by the backend. Runs at prerender, so crawlers and the first paint get
 * real events, and again in the browser, so a page baked days ago never lists one that has
 * already happened. Resolves to null when the API could not be reached, so a caller that already
 * has a list can keep it: the page stands on its own without them, and a build must not fail over
 * them.
 */
export function fetchFeaturedEvents(delays: readonly number[] = RETRY_DELAYS_MS): Promise<FeaturedEvent[] | null> {
    // Asking for the rows it shows: cutting a longer, date-ordered list here kept the soonest events and
    // dropped the biggest (DAU's festival, 410 going, never made the five).
    return fetch(`${apiBase()}/stats/featured-events?size=${EVENT_ROWS}`, {signal: AbortSignal.timeout(10_000)})
        .then(res => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
        .then((body: {events?: FeaturedEvent[]}) => body.events ?? [])
        .catch(() => delays.length === 0
            ? null
            : new Promise<FeaturedEvent[] | null>(resolve =>
                setTimeout(() => resolve(fetchFeaturedEvents(delays.slice(1))), delays[0])));
}
